'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { CheckCircle2 } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Plan {
    name: string;
}

interface AdvantagesSectionProps {
    plan: Plan;
    translationNamespace: string;
    advantageKeys: string[];
}

export function AdvantagesSection({
    plan,
    translationNamespace,
    advantageKeys,
}: AdvantagesSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const advantages = advantageKeys.map(key => t(key)).filter(Boolean);

    return (
        <SectionWrapper ref={sectionRef.ref} variant="default" padding="md" className="relative bg-white dark:bg-gray-950">
            <div className="container">
                <div className={cn(
                    "mb-12 text-center transition-all duration-700",
                    sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                )}>
                    <Typography variant="h2" className="mb-4 text-gray-900 dark:text-white">
                        {t('title')}
                    </Typography>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
                    {advantages.map((advantage: string, index: number) => (
                        <div
                            key={index}
                            className={cn(
                                "bg-gray-50 dark:bg-gray-800 rounded-lg p-5 flex items-start gap-3 transition-all duration-500",
                                sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                            )}
                            style={{ transitionDelay: `${100 + index * 50}ms` }}
                        >
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 dark:bg-primary-600 flex items-center justify-center mt-0.5">
                                <CheckCircle2 className="w-4 h-4 text-white" />
                            </div>
                            <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed flex-1">
                                {advantage}
                            </Typography>
                        </div>
                    ))}
                </div>
            </div>
        </SectionWrapper>
    );
}

